import { useEffect, useMemo, useState } from 'react';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { AutoRenewal, AutoRenewalStatus } from './types';

const ENDPOINT = '/services_api/storefront/v1/auto_renewals.json';

/** Worst first: what needs the customer to do something is what they came for. */
const STATUS_ORDER: AutoRenewalStatus[] = [
  'needs-attention',
  'active',
  'charged',
  'no-longer-available',
  'canceled',
];

const ACTIVE_STATUSES: AutoRenewalStatus[] = ['active', 'needs-attention', 'charged'];
const INACTIVE_STATUSES: AutoRenewalStatus[] = ['canceled', 'no-longer-available'];

function inStatuses(autoRenewals: AutoRenewal[], statuses: AutoRenewalStatus[]): AutoRenewal[] {
  return autoRenewals
    .filter((autoRenewal) => statuses.includes(autoRenewal.status))
    .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
}

/**
 * The customer's subscriptions, fetched once when the page mounts.
 *
 * All of it happens in the browser, as it did at the source. Nothing behind the login
 * wall is indexed, so there is nothing to gain by having the list in the first paint —
 * and a page whose props are the same for every customer is a page whose props and markup
 * can both be cached, which is worth rather more.
 *
 * The one thing this does that the source did not have to: it watches for a 401. The
 * controller has already turned away anyone who was not signed in when the page was
 * requested, so a 401 here means the session lapsed in between — the page is up, and the
 * only honest thing to draw is the login form. `loginUrl` is relative to the site root
 * like every other url the server hands over, so it goes through `buildUrl` too.
 */
export default function useAutoRenewals(loginUrl: string) {
  const { buildUrl } = useUrlGenerator();
  const [autoRenewals, setAutoRenewals] = useState<AutoRenewal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    fetch(buildUrl(ENDPOINT), { signal: controller.signal })
      .then((response) => {
        if (response.status === 401 || response.status === 403) {
          window.location.assign(buildUrl(loginUrl));

          // Nothing after this matters — the browser is leaving. Staying in the loading
          // state is deliberate: an error, or an empty list, would flash under the
          // navigation as "you have no subscriptions", which is not what happened.
          return null;
        }

        if (!response.ok) {
          throw new Error(`Auto renewals answered ${response.status}`);
        }

        return response.json() as Promise<AutoRenewal[]>;
      })
      .then((answer) => {
        if (answer === null) {
          return;
        }

        setAutoRenewals(answer ?? []);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }

        console.error('Failed to load auto renewals', error);
        setHasFailed(true);
        setIsLoading(false);
      });

    return () => controller.abort();
    // `buildUrl` is derived from the site, which does not change within a page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(() => inStatuses(autoRenewals, ACTIVE_STATUSES), [autoRenewals]);
  const inactive = useMemo(() => inStatuses(autoRenewals, INACTIVE_STATUSES), [autoRenewals]);

  return {
    active,
    inactive,
    // Counted off `active` rather than the whole list: every needs-attention
    // subscription is an active one, and counting them separately would be a second
    // place that has to agree with ACTIVE_STATUSES about that.
    needsAttentionCount: active.filter((autoRenewal) => autoRenewal.status === 'needs-attention').length,
    isLoading,
    hasFailed,
  };
}
